import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const empty = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export const Contacto = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa nombre, correo y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Mensaje enviado! Le contactaremos pronto.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar el mensaje.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="relative py-28 sm:py-36 bg-vnavy-800 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-vteal-light" />
            <span className="text-sm uppercase tracking-[0.3em] text-vteal-light font-semibold">Contacto</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-white leading-tight">
            Hablemos de su empresa
          </h2>
          <p className="mt-6 text-gray-400 leading-relaxed">
            Cuéntenos sobre su operación y le mostraremos cómo un solo proveedor puede darle más eficiencia y menos riesgos.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7 rounded-2xl bg-vnavy border border-white/10 p-8 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Nombre completo *</label>
              <Input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Su nombre"
                className="bg-vnavy-800 border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Correo electrónico *</label>
              <Input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="correo@empresa.com"
                className="bg-vnavy-800 border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Teléfono</label>
              <Input
                data-testid="contact-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+506 0000 0000"
                className="bg-vnavy-800 border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Empresa</label>
              <Input
                data-testid="contact-company"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="Nombre de su empresa"
                className="bg-vnavy-800 border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Servicio de interés</label>
            <Select value={form.service} onValueChange={(v) => set("service", v)}>
              <SelectTrigger
                data-testid="contact-service"
                className="bg-vnavy-800 border-white/15 text-white focus:ring-vteal-light"
              >
                <SelectValue placeholder="Seleccione un servicio" />
              </SelectTrigger>
              <SelectContent className="bg-vnavy-800 border-white/15 text-white">
                <SelectItem value="Recursos Humanos">Recursos Humanos</SelectItem>
                <SelectItem value="Asesoría Legal Laboral">Asesoría Legal Laboral</SelectItem>
                <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                <SelectItem value="Paquete Integral">Paquete Integral</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Mensaje *</label>
            <Textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Cuéntenos qué necesita..."
              rows={4}
              className="bg-vnavy-800 border-white/15 text-white placeholder:text-gray-600 focus-visible:ring-vteal-light resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-vteal-light px-8 py-4 text-vnavy font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Enviando..." : "Enviar mensaje"}
            {!loading && <Send className="h-4 w-4" />}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
